import { useEffect, useRef, useState } from "react";
import { Check, Copy, Mail } from "lucide-react";
import Button from "../../../components/ui/Button";
import { setTitle } from "../../../helpers/MetaTag";
import useCustomerMenu from "../../../hooks/useCustomerMenu";

export const SUPPORT_EMAIL = "support@memorydoodle.com";

/**
 * /help, from the menu's Help item (docs/design/HANDOFF.md: Help, TabHelp,
 * DeskHelp). The support address, a mail button, and a copy button for people
 * whose computer has no mail app set up ("Copied" with a check for about 2.5
 * seconds). Buttons stack on phones and sit side by side from tablet up. No
 * promised reply time.
 */
const Help = () => {
  const menu = useCustomerMenu();
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    setTitle("Help | MemoryDoodle");
    return () => clearTimeout(timer.current);
  }, []);

  // The older copy command works inside a tap where the Clipboard API can be refused (some iOS Safari setups).
  const copyWithCommand = () => {
    const area = document.createElement("textarea");
    area.value = SUPPORT_EMAIL;
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    area.setSelectionRange(0, SUPPORT_EMAIL.length);
    let ok = false;
    try {
      ok = document.execCommand("copy");
    } catch {
      ok = false;
    }
    document.body.removeChild(area);
    return ok;
  };

  const showCopied = () => {
    setCopied(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 2500);
  };

  const copy = async () => {
    if (copyWithCommand()) {
      showCopied();
      return;
    }
    try {
      await navigator.clipboard.writeText(SUPPORT_EMAIL);
      showCopied();
    } catch {
      // Neither worked: select the address so it can be copied by hand.
      const node = document.getElementById("help-email");
      if (!node) return;
      const range = document.createRange();
      range.selectNodeContents(node);
      window.getSelection()?.removeAllRanges();
      window.getSelection()?.addRange(range);
    }
  };

  return (
    <main className="mx-auto flex w-full max-w-[616px] flex-col gap-5 pb-12 md:gap-6">
      <div className="flex flex-col gap-1.5">
        <h1 className="m-0 font-heading text-[30px] font-normal leading-[1.1] text-md-dark md:text-[40px] lg:text-[44px]">How can we help?</h1>
        <p className="m-0 text-[15px] leading-snug text-md-muted md:text-[17px]">Questions about your book or your order? Email us and we'll write back.</p>
      </div>

      <section className="flex flex-col gap-5 rounded-2xl border border-md-pink-light-1 bg-white p-4 md:p-7">
        <div className="flex items-center gap-3 md:gap-3.5">
          {/* 40px on phones so the address fits on one line at 375 wide. */}
          <span aria-hidden="true" className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-md-pink/10 text-md-pink-dark md:h-12 md:w-12">
            <Mail className="h-5 w-5" />
          </span>
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="text-sm text-md-muted">Email us at</span>
            <span id="help-email" className="select-all text-[17px] font-bold text-md-dark [overflow-wrap:anywhere] md:text-[22px]">
              {SUPPORT_EMAIL}
            </span>
          </div>
        </div>
        <div className="flex flex-col gap-2.5 md:flex-row">
          <Button size="lg" fullWidth href={`mailto:${SUPPORT_EMAIL}`} icon={<Mail className="h-5 w-5" aria-hidden="true" />}>
            Email us
          </Button>
          <Button
            size="lg"
            variant="secondary"
            fullWidth
            onClick={copy}
            icon={copied ? <Check className="h-5 w-5 text-green-800" aria-hidden="true" /> : <Copy className="h-5 w-5" aria-hidden="true" />}
          >
            <span aria-live="polite">{copied ? "Copied" : "Copy email address"}</span>
          </Button>
        </div>
      </section>

      <section className="flex flex-col gap-3 rounded-2xl border border-md-pink-light-1 bg-white p-4 md:p-7">
        <h2 className="m-0 font-sans text-base font-bold text-md-dark">To help us answer faster, include:</h2>
        <ul className="m-0 flex list-none flex-col gap-2 p-0 text-[15px] text-[#4A4446]">
          {["The email you use to sign in", "Your book's title", "A screenshot, if something looks wrong"].map((item) => (
            <li key={item} className="flex items-center gap-2.5">
              <span aria-hidden="true" className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-md-pink" />
              {item}
            </li>
          ))}
        </ul>
      </section>

      <div className="flex justify-center">
        <Button variant="text" onClick={menu.goToMyBooks}>
          Back to My Books
        </Button>
      </div>
    </main>
  );
};

export default Help;
